import React, { useCallback, useContext, useEffect, useState } from 'react';

import Location from 'component/Search/Location';
import Sorting from 'component/Search/Sort/List';
import { HotelSortOption } from 'component/Search/Sort/List/__config/order';

import { AccommodationDataContext } from 'pages/hotels/context';
import { StartNewSearchScope } from 'pages/hotels/reducer/AccommodationList/types';

import classNames from 'classnames';
import { Feature } from 'config/features';

import {
    useCityFilter,
    useHotelSorting,
    useSetPagination,
} from 'hooks/form/search';
import { useResetChangedFilterList } from 'hooks/form/useChangedFilterList';
import { useFeatureList } from 'hooks/useFeature';
import { useTranslation } from 'hooks/useTranslation';

import { ReactComponent as IconInfoCircle } from 'media/svg/infoCircle.svg';

import Portal from 'utility/Bind/Portal/Portal';
import { getDecimalNumber } from 'utility/Format/Number';
import { getUrlParams } from 'utility/Provide/Url';

import { SortOrder } from 'types/Search/form';

import { ResponseStatus } from 'proto/Api/ResponseStatus';

import { getDestination, getType } from './__helper/getDestination';
import { getItemCount } from './__helper/getItemCount';
import Headline from './__partials/Headline';
import MapToggle from './__partials/MapToggle';

import styles from './Header.module.scss';

interface HeaderProps {
    target: string;
}

const Header = ({ target }: HeaderProps) => {
    const { t } = useTranslation();
    const headlineNode = document.getElementById('react-replace');
    const urlParamPage = getUrlParams('page');
    const currentPage = urlParamPage ? parseInt(urlParamPage) : 1;
    const accommodationContext = useContext(AccommodationDataContext);
    const { accommodationList, alternativeHotelList, responseStatus } =
        accommodationContext;
    const {
        locationFilterData: { regionList, cityList },
    } = accommodationContext;
    const isLoading =
        accommodationContext.isLoading ||
        responseStatus === ResponseStatus.Pending;
    const itemCount = getItemCount(
        accommodationList,
        alternativeHotelList,
        currentPage
    );
    const { priceMin, priceMax, meta } = accommodationList;
    const minPricePerPerson = meta?.travellerCount
        ? parseInt(getDecimalNumber(priceMin / meta.travellerCount, 0))
        : 0;
    const [showMap, isS3SemHotelEntryActive] = useFeatureList(
        Feature.HotelMap,
        Feature.S3SemHotelEntry
    );
    const [semBadge, setSemBadge] = useState(
        isS3SemHotelEntryActive ? getUrlParams('semBadge') : ''
    );
    const [headline, setHeadline] = useState(headlineNode?.innerHTML || '');
    const [hotelSorting, setHotelSorting] = useHotelSorting();
    const setPagination = useSetPagination();
    const resetChangedFilterList = useResetChangedFilterList();

    const [cityIdList] = useCityFilter();
    const onSelect_ = useCallback(
        (value: SortOrder) => {
            resetChangedFilterList();
            setHotelSorting(value);
            setPagination((prev) => ({
                ...prev,
                currentPage: 1,
            }));
        },
        [resetChangedFilterList, setHotelSorting, setPagination]
    );

    useEffect(() => {
        const destination = meta?.autoCompleterDestinationName
            ? getDestination(meta.autoCompleterDestinationName)
            : false;
        const destinationType = getType(destination);

        if (
            accommodationContext.searchStatus ===
            StartNewSearchScope.Destination
        ) {
            setSemBadge('');
        }

        if (!semBadge) {
            if (itemCount > 0) {
                if (destination) {
                    setHeadline(
                        t('TRANS-found-n-hotels-for-destination[i]', {
                            postProcess: 'interval',
                            count: destinationType,
                            resultCount: getDecimalNumber(itemCount, 0),
                            destination: destination,
                            label: t('TRANS-hotel[i]', {
                                postProcess: 'interval',
                                count: itemCount,
                            }),
                        })
                    );
                } else {
                    setHeadline(
                        t('TRANS-results-for-default-search[i]', {
                            postProcess: 'interval',
                            count: itemCount,
                            resultCount: itemCount,
                        })
                    );
                }
            } else {
                setHeadline(
                    t('TRANS-results-for-default-search[i]', {
                        postProcess: 'interval',
                        count: 0,
                    })
                );
            }
        }
    }, [
        accommodationContext.searchStatus,
        itemCount,
        meta?.autoCompleterDestinationName,
        semBadge,
        t,
    ]);

    return (
        <Portal root={target}>
            <>
                <div className={styles.Header}>
                    <Headline
                        text={headline}
                        badge={semBadge}
                        resultCount={itemCount}
                        minPricePerPerson={minPricePerPerson}
                        maxPrice={priceMax}
                        minPrice={priceMin}
                        isLoading={isLoading}
                    />
                    {showMap ? (
                        <MapToggle />
                    ) : (
                        <div
                            className={classNames(
                                styles.MapToggle,
                                'map-small-cnt'
                            )}
                        />
                    )}
                    <div className={styles.Filter}>
                        <div className={styles.RegionCityFilter}>
                            {cityList && (
                                <Location
                                    cityList={cityList}
                                    regionList={regionList}
                                />
                            )}
                        </div>
                        <Sorting
                            optionList={
                                // We only provide CityCenter Sorting for Searches with 1 CityId selected
                                cityIdList.size === 1
                                    ? HotelSortOption
                                    : HotelSortOption.filter((option) => {
                                          return (
                                              option.value !==
                                              SortOrder.DistanceCityCenter
                                          );
                                      })
                            }
                            current={
                                // We ignore sorting (Url) Param CityCenter when we do not have 1 CityId selected
                                !hotelSorting ||
                                (hotelSorting ===
                                    SortOrder.DistanceCityCenter &&
                                    cityIdList.size !== 1) ||
                                hotelSorting ===
                                    SortOrder.PersonalPopularityIndex // when logged in we get PerosnalPopularityIndex and match it to default Popularity
                                    ? SortOrder.Popularity
                                    : hotelSorting
                            }
                            onSelect={onSelect_}
                        />
                    </div>
                    {hotelSorting === SortOrder.Rating && (
                        <div className={styles.SortingInfo}>
                            <IconInfoCircle />
                            {t(
                                'TRANS-hotels-with-few-good-ratings-are-devalued'
                            )}
                        </div>
                    )}
                </div>
            </>
        </Portal>
    );
};

export default Header;
